import { useEffect } from "react";
import { X, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "@/lib/api";

/**
 * Click any note image to open this fullscreen viewer. Supports keyboard
 * navigation (arrow keys / Escape) and a one-click download button that
 * saves the original asset to the user's device.
 */
export default function ImageLightbox({ images, index, onIndexChange, onClose }) {
  useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && index < images.length - 1) onIndexChange(index + 1);
      if (e.key === "ArrowLeft" && index > 0) onIndexChange(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images, onIndexChange, onClose]);

  if (index == null || !images?.length) return null;
  const path = images[index];
  const url = fileUrl(path);
  const filename = (path || "image").split("/").pop() || "image";

  const download = async () => {
    try {
      const r = await fetch(url, { mode: "cors" });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      // Fallback: open in a new tab if the direct fetch is blocked by CORS.
      window.open(url, "_blank", "noopener");
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X className="h-5 w-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); download(); }}
        className="absolute top-4 right-16 h-10 px-4 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium"
        data-testid="lightbox-download"
      >
        <Download className="h-4 w-4" /> Download
      </button>

      {images.length > 1 && index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onIndexChange(index - 1); }}
          className="absolute left-4 h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Previous"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {images.length > 1 && index < images.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onIndexChange(index + 1); }}
          className="absolute right-4 h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Next"
          data-testid="lightbox-next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}

      <img
        src={url}
        alt=""
        className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />

      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-xs font-mono px-3 py-1 rounded-full bg-white/10">
          {index + 1} / {images.length}
        </div>
      )}
    </div>
  );
}
